/**
 * @file FontSampleText.tsx
 * @description Образец шрифта каталога (phase19.16 Р1; uiux-design-r1 §1):
 * текст образца выбранным шрифтом; файл грузится, только когда строка
 * видна (`IntersectionObserver`). Пока грузится — полоса-заготовка; не
 * загрузился — системный шрифт и подпись «образец не загрузился» (выбрать
 * шрифт всё равно можно). Свои шрифты магазина (Р1-7) — через реестр
 * контекста `useShopFonts`.
 */

import { useEffect, useRef, useState } from "react";

import { Skeleton } from "../../ui/skeleton";
import { useShopFonts } from "../shop-fonts-context";
import { useSiteView } from "../site-view-context";
import { useFontSample } from "../../../hooks/site-view/useFontSample";

interface Props {
  fontKey: string;
  text: string;
  weight?: number;
  /** Кегль образца, px (16 — текст, 24 — заголовок). */
  size?: number;
  lines?: 1 | 2;
}

export function FontSampleText({ fontKey, text, weight = 400, size = 16, lines = 1 }: Props) {
  const { siteUrl } = useSiteView();
  const ref = useRef<HTMLSpanElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { registry } = useShopFonts();
  const sample = useFontSample(fontKey, weight, visible, siteUrl, registry);

  return (
    <span ref={ref} className="flex min-w-0 flex-col gap-1">
      {sample.status === "loading" && visible ? (
        <Skeleton className="h-5 w-4/5 rounded" aria-label="Образец загружается" />
      ) : (
        <span
          className={lines === 1 ? "truncate text-on-surface" : "line-clamp-2 text-on-surface"}
          style={{ fontFamily: sample.fontFamily, fontWeight: sample.weight, fontSize: size, lineHeight: 1.3 }}
        >
          {text}
        </span>
      )}
      {sample.status === "error" ? <span className="type-caption text-on-surface-variant">образец не загрузился</span> : null}
    </span>
  );
}
